import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { Link, useBlocker } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import invoiceService from '@api/v1/invoiceService'
import { getTenant } from '@config/tenant'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import DataGrid from '@components/common/DataGrid'
import ControlCheckbox from '@components/common/ControlCheckbox'
import Modal from '@components/common/Modal'
import {
  OUTCOME,
  money,
  quantity,
  readPeriodInputs,
  realDay,
} from '@utils/billGeneration'
import * as billRun from '@utils/billRun'

/*
 * Spec 158 (CC-54030) — Generate Bills, the way Centerbase's Billing Home does it.
 *
 * The page used to post AutoGenerateBills' date fields with no matter, read an
 * `invoiceIdList` that action never returns, and never finalized. It now lists the
 * matters Billing Home would list for a saved billing setting and period, lets the
 * user pick them, confirms exactly what will be asked of Centerbase, and generates
 * one matter's bill at a time through cb-api (GetResultSetId + GenerateBills).
 *
 * Duplicate safety is the design constraint. A second run bills a matter again, so:
 *  - the run lives in utils/billRun.js, not in this page: one run at a time, however
 *    often Generate is pressed and however often the page mounts, and never a retry;
 *  - only cb-api's explicit "not sent" counts as "nothing was generated" — a timeout,
 *    an edge error or an unreadable answer is UNKNOWN and stops the run, and so does
 *    CB's "no bill" (it can hide a bill CB created; utils/billGeneration.js);
 *  - a matter whose bill was created, or may have been, is locked for the session —
 *    across a navigation away and back, and across a reload where the browser keeps
 *    session storage — and it is locked BEFORE its request is sent;
 *  - while a run is in flight, leaving the page inside the app asks first (react-router's
 *    blocker), and so does closing or reloading the tab (billRun's beforeunload);
 *  - after the run the list is read again, so CB's own figures and last bill show
 *    what happened (utils/billGeneration.js).
 * Split-billed matters are listed but not offered (cb-api marks them; spec 158 §3.6).
 *
 * There is no design for this page yet (CC-54031). It reuses the existing controls:
 * DataGrid, ControlCheckbox, Modal, .select-rc / .input-rc / .btn-cb-*.
 */

const key = (id) => String(id).toLowerCase()

const fmtDay = (iso) => (iso ? dayjs(iso).format('MMM D, YYYY') : '')

const periodText = (period) => (period.startDate
  ? `dated ${fmtDay(period.startDate)} through ${fmtDay(period.endDate)}`
  : `dated on or before ${fmtDay(period.endDate)}`)

const OUTCOME_BADGE = {
  [OUTCOME.CREATED]: { label: 'Bill created', cls: 'bg-green-50 text-green-800 border-green-700' },
  [OUTCOME.NONE]: { label: 'No bill reported', cls: 'bg-amber-50 text-amber-900 border-amber-700' },
  [OUTCOME.REFUSED]: { label: 'Not generated', cls: 'bg-red-50 text-red-700 border-red-700' },
  [OUTCOME.UNKNOWN]: { label: 'Outcome unknown', cls: 'bg-amber-50 text-amber-900 border-amber-700' },
  [OUTCOME.NOT_ATTEMPTED]: { label: 'Not sent', cls: 'bg-gray-50 text-gray-700 border-gray-500' },
}

const REREAD_NOTE = {
  reading: 'Reading the list again to show what Centerbase now has…',
  failed: 'The list could not be read again, so the results above are as Centerbase reported them.',
  offline: 'The list was not read again because the browser is offline. Reload the page once you are back online to see Centerbase’s figures.',
}

const ReadFailed = ({ what, query, state, nothingIs }) => (
  state === 'blocked' ? (
    <div role="alert" className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
      <p className="font-medium">Can’t reach the server, so we haven’t loaded {what}.</p>
      <p className="mt-1 text-amber-800">This is not {nothingIs} — it was never requested. Your connection looks offline; check it and try again.</p>
      <button type="button" onClick={() => query.refetch()} className="btn-cb-toolbar mt-3">Try again</button>
    </div>
  ) : (
    <div role="alert" className="mb-6 rounded-xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
      <p className="font-medium">Couldn’t load {what}: {query.error?.response?.data?.error || query.error?.message}</p>
      <p className="mt-1">This is not {nothingIs} — nothing was read.</p>
      <button type="button" onClick={() => query.refetch()} className="btn-cb-toolbar mt-3">Try again</button>
    </div>
  )
)

const BillLinks = ({ ids, number }) => (
  <span className="inline-flex flex-wrap gap-x-3">
    {ids.map((id, i) => (
      <Link key={id} to={`/invoices/${id}`} className="text-cb-accent-text underline">
        {ids.length === 1 && number ? `Open bill #${number}` : `Open bill${ids.length > 1 ? ` ${i + 1}` : ''}`}
      </Link>
    ))}
  </span>
)

const ResultRow = ({ r }) => {
  const badge = OUTCOME_BADGE[r.outcome]
  return (
    <li data-testid="generate-result" data-outcome={r.outcome} className="flex flex-col gap-1 py-3 sm:flex-row sm:items-start sm:gap-4">
      <span className="font-medium text-gray-900 sm:w-1/3">{r.matterName}</span>
      <span className="flex-1 text-sm text-gray-700">
        <span className={`mr-2 inline-block rounded border px-2 py-0.5 text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
        {r.outcome === OUTCOME.CREATED && <BillLinks ids={r.invoiceIds} number={r.billNumber} />}
        {r.message && <span className="mt-1 block">{r.message}</span>}
        {r.newBillSeen && (
          <span className="mt-1 block" data-testid="new-bill-seen">
            A new bill{r.newBillSeen.number ? ` (#${r.newBillSeen.number})` : ''} now shows for this matter, so Centerbase most likely created it.{' '}
            <BillLinks ids={[r.newBillSeen.id]} number={r.newBillSeen.number} />
          </span>
        )}
      </span>
    </li>
  )
}

const generateLabel = (count) => (count === 0 ? 'Generate bills…' : `Generate ${count} bill${count === 1 ? '' : 's'}…`)

const summarize = (results) => {
  const n = (o) => results.filter((r) => r.outcome === o).length
  const parts = [
    [n(OUTCOME.CREATED), 'created'],
    [n(OUTCOME.NONE), 'with no bill reported'],
    [n(OUTCOME.REFUSED), 'not generated'],
    [n(OUTCOME.UNKNOWN), 'with an unknown outcome'],
    [n(OUTCOME.NOT_ATTEMPTED), 'not sent'],
  ].filter(([c]) => c > 0)
  return parts.map(([c, label]) => `${c} ${label}`).join(' · ')
}

const progressText = (run) => {
  if (run.phase === 'running' && run.progress) {
    const { index, total, matterName } = run.progress
    return `Generating bill ${index + 1} of ${total}: ${matterName}…${run.stopRequested ? ' Stopping after this matter.' : ''}`
  }
  return run.phase === 'done' ? `Finished: ${summarize(run.results)}.` : ''
}

const BillGeneration = () => {
  useDocumentTitle('Generate Bills')
  const queryClient = useQueryClient()
  const tenant = getTenant()

  const optionsQuery = useQuery({
    queryKey: ['generate-bills', 'options'],
    queryFn: () => invoiceService.getGenerateOptions(),
    refetchOnWindowFocus: false,
  })
  const optionsState = useReadState('generate-bills-options', optionsQuery)
  const options = optionsState === 'ready' ? optionsQuery.data.data : null

  const [settingsChoice, setSettingsChoice] = useState(null)
  const defaultSettingId = options?.settings.find((s) => s.isDefault)?.id ?? options?.settings[0]?.id ?? null
  const settingsId = settingsChoice ?? defaultSettingId
  const settingName = options?.settings.find((s) => s.id === settingsId)?.name ?? ''

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState(() => dayjs().endOf('month').format('YYYY-MM-DD'))
  const [issueDate, setIssueDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const period = useMemo(() => readPeriodInputs(startDate, endDate), [startDate, endDate])
  const issueDay = realDay(issueDate)

  const listEnabled = !!options && !options.timeEntryApproval && settingsId != null && !period.error
  const candidatesKey = (p) => ['generate-bills', 'candidates', p.settingsId, p.startDate ?? null, p.endDate ?? null]
  const candidatesRequest = (p) => ({ settingsId: p.settingsId, ...(p.startDate ? { startDate: p.startDate } : {}), endDate: p.endDate })
  const candidatesQuery = useQuery({
    queryKey: candidatesKey({ settingsId, ...period }),
    queryFn: () => invoiceService.getGenerateCandidates(candidatesRequest({ settingsId, ...period })),
    enabled: listEnabled,
    refetchOnWindowFocus: false,
  })
  const candidatesState = useReadState('generate-bills-candidates', candidatesQuery)
  const list = candidatesState === 'ready' ? candidatesQuery.data.data : null
  const rows = list?.rows

  // The run, its results and the locks outlive this page (utils/billRun.js).
  const run = useSyncExternalStore(billRun.subscribe, billRun.getSnapshot)
  const running = run.phase === 'running'
  const locked = billRun.locksFor(tenant)

  const [selected, setSelected] = useState(() => new Set())
  const [pendingRun, setPendingRun] = useState(null)

  // Leaving inside the app while a run is in flight asks first: the in-flight matter
  // cannot be recalled, and the rest would not be sent.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => billRun.isRunning() && currentLocation.pathname !== nextLocation.pathname)
  useEffect(() => {
    // The run ended while the question was open: nothing is left to protect.
    if (blocker.state === 'blocked' && !running) blocker.proceed()
  }, [blocker, running])
  // A page that goes away anyway stops the run before its next matter.
  useEffect(() => () => billRun.requestStop(), [])

  // Focus follows the run: to the results when one starts here (the dialog that started
  // it is gone, and the button that opened that dialog is disabled), and back to them
  // when it ends with nothing focused (the Stop button is gone).
  const resultsHeadingRef = useRef(null)
  const runsSeen = useRef(run.runCount)
  useEffect(() => {
    if (run.runCount !== runsSeen.current) {
      runsSeen.current = run.runCount
      resultsHeadingRef.current?.focus()
    }
  }, [run.runCount])
  const phaseSeen = useRef(run.phase)
  useEffect(() => {
    // Only a run that ends while this page is open: coming back to a finished run
    // leaves focus where the navigation put it.
    const ended = phaseSeen.current === 'running' && run.phase === 'done'
    phaseSeen.current = run.phase
    if (ended && (!document.activeElement || document.activeElement === document.body)) {
      resultsHeadingRef.current?.focus()
    }
  }, [run.phase])

  const selectedRows = useMemo(
    () => (rows || []).filter((r) => selected.has(key(r.matterId)) && !r.ineligibleReason && !locked.has(key(r.matterId))),
    [rows, selected, locked]
  )
  const readyRows = useMemo(() => (rows || []).filter((r) => !r.ineligibleReason && !locked.has(key(r.matterId))), [rows, locked])
  const splitCount = useMemo(() => (rows || []).filter((r) => r.split).length, [rows])

  const resetSelection = () => setSelected(new Set())
  const toggle = (r) => setSelected((prev) => {
    const next = new Set(prev)
    if (next.has(key(r.matterId))) next.delete(key(r.matterId))
    else next.add(key(r.matterId))
    return next
  })

  const columns = useMemo(() => [
    {
      id: 'select',
      header: () => <span className="sr-only">Select</span>,
      enableSorting: false,
      enableColumnFilter: false,
      size: 48,
      cell: ({ row }) => {
        const r = row.original
        return (
          <ControlCheckbox
            hit24
            aria-label={`Select ${r.matterName}`}
            checked={selected.has(key(r.matterId))}
            disabled={r.ineligibleReason != null || locked.has(key(r.matterId)) || running}
            onChange={() => toggle(r)}
          />
        )
      },
    },
    {
      accessorKey: 'matterName',
      header: 'Matter',
      cell: ({ row }) => {
        const r = row.original
        const lock = locked.get(key(r.matterId))
        let note = null
        if (lock === OUTCOME.CREATED) note = <span className="text-green-800">Billed in this session.</span>
        else if (lock === billRun.IN_FLIGHT) note = <span className="text-amber-900">Being generated now.</span>
        else if (lock === OUTCOME.UNKNOWN) note = <span className="text-amber-900">Outcome unknown — check this matter’s bills in Centerbase before generating it again.</span>
        else if (lock === OUTCOME.NONE) note = <span className="text-amber-900">No bill reported — check this matter’s bills in Centerbase before generating it again.</span>
        else if (r.ineligibleReason) note = r.ineligibleReason
        else if (r.statementOnly) note = 'Nothing unbilled in this period — Centerbase generates a statement.'
        return (
          <div>
            <span className="text-gray-900">{r.matterName}</span>
            {note && <span className="mt-0.5 block text-xs text-gray-600" data-testid="matter-note">{note}</span>}
          </div>
        )
      },
    },
    { accessorKey: 'unbilledHours', header: 'Unbilled hours', meta: { align: 'right' }, cell: ({ getValue }) => quantity(getValue()) },
    { accessorKey: 'unbilledFees', header: 'Unbilled fees', meta: { align: 'right' }, cell: ({ getValue }) => money(getValue()) },
    { accessorKey: 'unbilledExpenses', header: 'Unbilled expenses', meta: { align: 'right' }, cell: ({ getValue }) => money(getValue()) },
    {
      id: 'lastBill',
      header: 'Last bill',
      accessorFn: (r) => r.lastBill?.date ?? '',
      cell: ({ row }) => {
        const b = row.original.lastBill
        if (!b) return <span className="text-gray-600">None</span>
        return (
          <span>
            {b.number ? `#${b.number}` : 'No number'}
            {b.date ? ` · ${fmtDay(b.date)}` : ''}
            {` · ${b.posted ? 'posted' : 'not posted'}`}
          </span>
        )
      },
    },
  ], [selected, locked, running])

  const openConfirm = () => {
    if (running || selectedRows.length === 0 || period.error || !issueDay) return
    setPendingRun({
      matters: selectedRows.map((r) => ({ matterId: r.matterId, matterName: r.matterName, statementOnly: r.statementOnly })),
      lastBillBefore: new Map(selectedRows.map((r) => [key(r.matterId), r.lastBill?.id ?? null])),
      period: { settingsId, startDate: period.startDate, endDate: period.endDate },
      issueDate: issueDay,
      settingName,
    })
  }
  const closeConfirm = () => setPendingRun(null)

  // Every press goes to billRun.startRun, which starts nothing while a run is in flight:
  // presses that land before React re-renders the button away are dropped, not queued.
  const startRun = () => {
    if (!pendingRun) return
    const p = pendingRun
    billRun.startRun({
      tenant,
      matters: p.matters,
      period: p.period,
      issueDate: p.issueDate,
      lastBillBefore: p.lastBillBefore,
      generate: (request) => invoiceService.generateBill(request),
      // A matter that was sent is no longer selected; one never sent stays selected.
      onResult: (r) => {
        if (r.outcome === OUTCOME.NOT_ATTEMPTED) return
        setSelected((prev) => {
          if (!prev.has(key(r.matterId))) return prev
          const next = new Set(prev)
          next.delete(key(r.matterId))
          return next
        })
      },
      isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
      // The same query the list reads, so the list on screen updates with it.
      reread: async () => {
        const body = await queryClient.fetchQuery({
          queryKey: candidatesKey(p.period),
          queryFn: () => invoiceService.getGenerateCandidates(candidatesRequest(p.period)),
          staleTime: 0,
          retry: false,
        })
        return body?.data?.rows ?? null
      },
    })
    setPendingRun(null)
  }

  const n = pendingRun?.matters.length ?? 0
  const statements = pendingRun?.matters.filter((m) => m.statementOnly).length ?? 0
  const announcement = running && run.stopRequested
    ? `Stopping after ${run.progress?.matterName ?? 'this matter'}.`
    : progressText(run)

  return (
    <div className="max-w-5xl mx-auto">
      {/* Mounted from the start, so the first progress message is a change a screen reader announces. */}
      <p className="sr-only" aria-live="polite" aria-atomic="true" data-testid="generate-announcer">{announcement}</p>

      <div className="mb-6">
        <h1 className="text-2xl font-display text-gray-900">Generate Bills</h1>
        <p className="text-sm text-gray-600 mt-1">
          Pick the matters to bill. Centerbase generates one bill per matter, using your firm’s Billing Home settings.
        </p>
      </div>

      {(optionsState === 'loading' || optionsState === 'idle') && (
        <div className="p-8 flex justify-center"><LoadingSpinner message="Loading billing settings..." /></div>
      )}
      {(optionsState === 'failed' || optionsState === 'blocked') && (
        <ReadFailed what="your firm’s billing settings" query={optionsQuery} state={optionsState} nothingIs="a firm with no billing settings" />
      )}

      {options?.timeEntryApproval && (
        <div data-testid="approval-refusal" className="mb-6 rounded-xl border border-gray-300 bg-gray-50 p-5 text-sm text-gray-800">
          <p className="font-medium">Your firm approves time entries before billing.</p>
          <p className="mt-1">cb2 does not generate bills for firms that do yet. Generate them in Centerbase, which checks each matter’s unapproved time first.</p>
        </div>
      )}

      {options && !options.timeEntryApproval && options.settings.length === 0 && (
        <div className="mb-6 rounded-xl border border-gray-300 bg-gray-50 p-5 text-sm text-gray-800">
          Centerbase lists no billing settings for your firm, so there are no matters to list. Set one up in Centerbase’s Billing Home.
        </div>
      )}

      {options && !options.timeEntryApproval && options.settings.length > 0 && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">What to bill</h2>
            </div>
            <div className="p-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label htmlFor="gb-setting" className="block text-sm font-medium text-gray-700 mb-1">Billing setting</label>
                <select
                  id="gb-setting"
                  className="select-rc w-full"
                  value={settingsId ?? ''}
                  disabled={running}
                  onChange={(e) => { setSettingsChoice(Number(e.target.value)); resetSelection() }}
                >
                  {options.settings.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}{s.isDefault ? ' (default)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="gb-start" className="block text-sm font-medium text-gray-700 mb-1">Entries from (optional)</label>
                <input id="gb-start" type="date" className="input-rc input-rc-lg w-full" value={startDate} disabled={running}
                  onChange={(e) => { setStartDate(e.target.value); resetSelection() }} />
              </div>
              <div>
                <label htmlFor="gb-end" className="block text-sm font-medium text-gray-700 mb-1">Entries through</label>
                <input id="gb-end" type="date" className="input-rc input-rc-lg w-full" value={endDate} disabled={running}
                  onChange={(e) => { setEndDate(e.target.value); resetSelection() }} />
              </div>
              <div>
                <label htmlFor="gb-issue" className="block text-sm font-medium text-gray-700 mb-1">Issue date</label>
                <input id="gb-issue" type="date" className="input-rc input-rc-lg w-full" value={issueDate} disabled={running}
                  onChange={(e) => setIssueDate(e.target.value)} />
              </div>
            </div>
            <div className="px-6 pb-5 text-sm text-gray-600">
              {period.error
                ? <p role="alert" className="text-red-700">{period.error}</p>
                : <p>Bills include unbilled time and expenses {periodText(period)}.</p>}
              {!issueDay && <p role="alert" className="mt-1 text-red-700">Choose the issue date for the bills.</p>}
            </div>
          </div>

          {(candidatesState === 'loading' || (candidatesState === 'idle' && listEnabled)) && (
            <div className="p-8 flex justify-center"><LoadingSpinner message="Loading matters..." /></div>
          )}
          {(candidatesState === 'failed' || candidatesState === 'blocked') && (
            <ReadFailed what="the matters to bill" query={candidatesQuery} state={candidatesState} nothingIs="a firm with nothing to bill" />
          )}

          {list && (
            <div className="mb-6">
              {!list.complete && (
                <p role="alert" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Centerbase counted {list.rowCount} matters; {list.rows.length} are shown. Choose a narrower billing setting or period to see the rest.
                </p>
              )}
              {splitCount > 0 && (
                <p data-testid="split-note" className="mb-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-800">
                  {splitCount === 1 ? '1 of these matters is' : `${splitCount} of these matters are`} split-billed. Centerbase bills
                  {splitCount === 1 ? ' it' : ' each of them'} together with the matters it splits with, and cb2 sends one matter at a
                  time, so {splitCount === 1 ? 'it is' : 'they are'} not offered here. Generate {splitCount === 1 ? 'it' : 'them'} in Centerbase.
                </p>
              )}
              {list.rows.length === 0 ? (
                <p data-testid="no-matters" className="rounded-xl border border-gray-100 bg-white p-8 text-center text-sm text-gray-600">
                  Centerbase lists no matters to bill for “{settingName}” with entries {periodText(period)}.
                </p>
              ) : (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-3">
                    <span className="text-sm text-gray-700" data-testid="selected-count">
                      {selectedRows.length} of {readyRows.length} ready {readyRows.length === 1 ? 'matter' : 'matters'} selected
                    </span>
                    <button type="button" className="btn-cb-toolbar" disabled={running || readyRows.length === 0}
                      onClick={() => setSelected(new Set(readyRows.map((r) => key(r.matterId))))}>
                      Select all ready
                    </button>
                    <button type="button" className="btn-cb-toolbar" disabled={running || selectedRows.length === 0} onClick={resetSelection}>
                      Clear selection
                    </button>
                    <span className="flex-1" />
                    <button type="button" className="btn-cb-primary" disabled={running || selectedRows.length === 0 || !!period.error || !issueDay}
                      onClick={openConfirm}>
                      {running ? 'Generating…' : generateLabel(selectedRows.length)}
                    </button>
                  </div>
                  <DataGrid
                    columns={columns}
                    data={list.rows}
                    enableExport={false}
                    rowNoun="matter"
                    tableLabel="Matters to bill"
                    emptyMessage="No matters match the column filters."
                  />
                </>
              )}
            </div>
          )}
        </>
      )}

      {run.phase !== 'idle' && (
        <section aria-labelledby="gb-results" className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
            <h2 id="gb-results" ref={resultsHeadingRef} tabIndex={-1} className="text-base font-semibold text-gray-900">Results</h2>
            <span className="flex-1" />
            {running && (
              <button type="button" className="btn-cb-toolbar" onClick={billRun.requestStop}>
                {run.stopRequested ? 'Stopping after this matter…' : 'Stop after this matter'}
              </button>
            )}
          </div>
          <div className="px-6 py-4">
            <p data-testid="generate-progress" className="text-sm text-gray-700">{progressText(run)}</p>
            {run.phase === 'done' && REREAD_NOTE[run.reread] && (
              <p data-testid="generate-reread" className="mt-1 text-sm text-gray-600">{REREAD_NOTE[run.reread]}</p>
            )}
            <ul className="mt-2 divide-y divide-gray-100">
              {run.results.map((r) => <ResultRow key={r.matterId} r={r} />)}
            </ul>
          </div>
        </section>
      )}

      {pendingRun && !running && (
        <Modal
          title={`Generate ${n} bill${n === 1 ? '' : 's'}?`}
          subtitle={`Billing setting: ${pendingRun.settingName}`}
          labelledBy="gb-confirm-title"
          onClose={closeConfirm}
          footer={(
            <>
              <span className="flex-1" />
              <button type="button" className="btn-cb-toolbar" onClick={closeConfirm}>
                Cancel
              </button>
              <button type="button" className="btn-cb-primary" onClick={startRun}>
                Generate {n} bill{n === 1 ? '' : 's'}
              </button>
            </>
          )}
        >
          <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
            <p>
              Centerbase will generate one bill for each matter below, one matter at a time, dated{' '}
              <strong>{fmtDay(pendingRun.issueDate)}</strong>, from unbilled time and expenses {periodText(pendingRun.period)}.
            </p>
            {statements > 0 && (
              <p>{statements} of them {statements === 1 ? 'has' : 'have'} nothing unbilled in the period, so Centerbase generates a statement for {statements === 1 ? 'it' : 'each'}.</p>
            )}
            <ul data-testid="confirm-matters" className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 px-3 py-2">
              {pendingRun.matters.map((m) => <li key={m.matterId} className="py-0.5">{m.matterName}</li>)}
            </ul>
            <p>
              For each new bill Centerbase may also mark the matter’s company as a client, run your firm’s invoice
              workflows (which can send email), and create a document folder.
            </p>
            <p>
              If Centerbase does not confirm a matter’s bill, the run stops there and that matter is locked for the rest of
              this session, so nothing is billed twice. Nothing is retried automatically.
            </p>
          </div>
        </Modal>
      )}

      {blocker.state === 'blocked' && running && (
        <Modal
          title="Leave while bills are being generated?"
          labelledBy="gb-leave-title"
          onClose={() => blocker.reset()}
          footer={(
            <>
              <span className="flex-1" />
              <button type="button" className="btn-cb-toolbar" onClick={() => blocker.reset()}>
                Stay on this page
              </button>
              <button type="button" className="btn-cb-primary" onClick={() => { billRun.requestStop(); blocker.proceed() }}>
                Leave and stop the run
              </button>
            </>
          )}
        >
          <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
            <p>
              Centerbase is generating the bill for <strong>{run.progress?.matterName}</strong> now, and that request cannot be recalled.
            </p>
            <p>
              If you leave, the run stops after it and the matters after it are not sent. What Centerbase answers for this matter is
              kept: the matter stays locked, and its result shows here when you come back.
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default BillGeneration
